const MAX_RECURSION_DEPTH = 10;
/**
 * Sets the disabled attribute to true on this element and
 * recursively applies it to all its children up to a max depth.
 */
export function setDisabledAttribute(
  element: Element,
  operation: 'add' | 'remove' = 'add',
  depth: number = 0
): void {
  const attributeChange =
    operation === 'add'
      ? (i: number) =>
          element.children.item(i)?.setAttribute('disabled', 'true')
      : (i: number) => element.children.item(i)?.removeAttribute('disabled');

  for (let i = 0; i < element.children.length; i++) {
    const currentDepth = depth + 1;
    attributeChange(i);
    if (
      (element.children.item(i)?.children.length ?? 0) > 0 &&
      currentDepth < MAX_RECURSION_DEPTH
    ) {
      setDisabledAttribute(element.children.item(i)!, operation, currentDepth);
    }
  }
}

/**
 * Adds a class to this element and
 * recursively applies it to all its children up to a max depth.
 */
export function setClassAttribute(
  element: Element,
  classString: string,
  operation: 'add' | 'remove' = 'add',
  depth: number = 0
): void {
  const attributeChange =
    operation === 'add'
      ? (i: number, currentClass: string) =>
          element.children
            .item(i)
            ?.setAttribute('class', mergeClasses(currentClass, classString))
      : (i: number, currentClass: string) =>
          element.children
            .item(i)
            ?.setAttribute('class', removeClasses(currentClass, classString));

  for (let i = 0; i < element.children.length; i++) {
    const currentDepth = depth + 1;
    const currentClass = element.children.item(i)?.getAttribute('class') ?? '';
    attributeChange(i, currentClass);
    if (
      (element.children.item(i)?.children.length ?? 0) > 0 &&
      currentDepth < MAX_RECURSION_DEPTH
    ) {
      setClassAttribute(
        element.children.item(i)!,
        classString,
        operation,
        currentDepth
      );
    }
  }
}

export function mergeClasses(current: string, extra?: string): string {
  return extra ? `${current} ${extra}` : current;
}

export function removeClasses(current: string, extra?: string): string {
  return extra ? current.split(extra).join('') : current;
}

const WRITTEN_ATTRS = Symbol('domUtilsWrittenAttrs');

type AttrCachedElement = Element & {
  [WRITTEN_ATTRS]?: Map<string, string | null>;
};

/**
 * Sets (or removes, for `null`) an attribute only when its value changed since
 * the last call for this element. Skipping unchanged writes avoids DOM calls
 * and the style invalidation they can trigger.
 */
export function setAttrIfChanged(
  element: Element,
  name: string,
  value: string | number | null
): void {
  const next = value === null ? null : String(value);
  const cached = element as AttrCachedElement;
  let written = cached[WRITTEN_ATTRS];
  if (!written) {
    written = new Map();
    cached[WRITTEN_ATTRS] = written;
  } else if (written.has(name) && written.get(name) === next) {
    return;
  }
  written.set(name, next);
  if (next === null) {
    element.removeAttribute(name);
  } else {
    element.setAttribute(name, next);
  }
}
